<script lang="ts" setup>
import { useI18n } from '#i18n'
import 'vue3-carousel/dist/carousel.css'
import { Carousel, Slide, Navigation } from 'vue3-carousel'

const { locale } = useI18n()

const { data, pending, error } = await useAsyncData('homepage', () =>
  authFetch('homefocus')
)
const dataTexts = await authFetch('hometexts')
const breakpoints = ref({
  700: {
    itemsToShow: 3,
    itemsToScroll: 2,
    snapAlign: 'center',
  },
  1024: {
    itemsToShow: 4,
    itemsToScroll: 2,
    snapAlign: 'start',
  },
})

function tranformUrlTexts(text?: string): string {
  if (!text) return ''
  return text.replaceAll(
    /<a href="([^"]+)"/g,
    '<a target="_blank" rel="noopener noreferrer" href="$1"'
  )
}

// Focus
const focusText = computed<string>(() => {
  return tranformUrlTexts(dataTexts?.focusText)
})
const focusTextEn = computed<string>(() => {
  return tranformUrlTexts(dataTexts?.focusTextEn)
})

function formatCard(item) {
  return {
    ...item,
    title: item.cardTitle,
    titleEn: item.cardTitleEn,
    subtitle: item.cardSubTitle,
    excerpt: item.cardDescription,
    excerptEn: item.cardDescriptionEn,
    image: item.cardImage,
  }
}

const focuses = computed<ResourceCards>(() => {
  if (pending.value || error.value) return []
  return data.value?.focus
    .filter((focus) => focus)
    .map(formatCard)
    .sort((a, b) => a.order - b.order)
})

// Exhibition
const exhibitionText = computed<string>(() => {
  return tranformUrlTexts(dataTexts?.exhibitionText)
})
const exhibitionTextEn = computed<string>(() => {
  return tranformUrlTexts(dataTexts?.exhibitionTextEn)
})
const exhibition = computed<ResourceCards>(() => {
  if (pending.value || error.value) return []
  return data.value?.exhibition
    .filter((item) => item)
    .map(formatCard)
    .sort((a, b) => a.order - b.order)
})

// Latest publications
const latestPublications = computed<ResourceCards>(() => {
  if (pending.value || error.value) return []
  return data.value?.actualities
    .filter((actuality) => actuality)
    .map(formatCard)
    .sort((a, b) => a.order - b.order)
})
//Floras
const floraText = computed<string>(() => {
  return tranformUrlTexts(dataTexts?.floraText)
})
const floraTextEn = computed<string>(() => {
  return tranformUrlTexts(dataTexts?.floraTextEn)
})
const floras = computed<ResourceCards>(() => {
  if (pending.value || error.value) return []
  return data.value?.floras
    .filter((flora) => flora)
    .map(formatCard)
    .sort((a, b) => a.order - b.order)
})

const latestPublicationsGroups = [
  {
    title: 'Biographies',
    titleEn: 'Biographies',
    filterFn: ({ type }) => type === 'biography',
  },
  {
    title: 'Parcours et Analyses',
    titleEn: 'Surveys of works and Analyses',
    filterFn: ({ type }) => type === 'analysis' || type === 'workcourse',
  },
  {
    title: 'Concerts',
    titleEn: 'Concerts',
    filterFn: ({ subType }) =>
      subType === 'Archives concerts' || subType === 'Concert',
  },
  {
    title: 'Conférences',
    titleEn: 'Conferences',
    filterFn: ({ subType }) =>
      subType === 'Conférences' || subType === 'Séminaire / Conférence',
  },
  {
    title: 'Autres',
    titleEn: 'Others',
    filterFn: ({ type, subType }) =>
      type !== 'analysis' &&
      type !== 'workcourse' &&
      type !== 'biography' &&
      subType !== 'Archives concerts' &&
      subType !== 'Conférences' &&
      subType !== 'Séminaire / Conférence' &&
      subType !== 'Concert',
  },
]
</script>

<template>
  <div class="mx-auto max-w-7xl px-6 py-10 md:px-4 md:py-12">
    <section>
      <h2 class="text-mobile-2xl font-semibold md:text-2xl">
        {{ $t('index.focus_title') }}
      </h2>
      <span
        v-if="locale === 'fr'"
        class="markdown-links mt-2 text-mobile-lg md:mt-4 md:text-lg"
        v-html="focusText"
      />
      <span
        v-else
        class="markdown-links mt-2 text-mobile-lg md:mt-4 md:text-lg"
        v-html="focusTextEn"
      />

      <div v-if="pending" class="mx-auto flex">
        <carousel
          :items-to-show="2"
          :items-to-scroll="1"
          :breakpoints="breakpoints"
        >
          <slide v-for="i in 4" :key="i">
            <CardSkeleton />
          </slide>
        </carousel>
      </div>
      <div v-else>
        <carousel
          :items-to-show="2"
          :items-to-scroll="1"
          :breakpoints="breakpoints"
        >
          <slide v-for="card in focuses" :key="card.id">
            <CardResource :key="card.id" :resource="card" />
          </slide>

          <template #addons>
            <navigation />
            <!-- <pagination /> -->
          </template>
        </carousel>
      </div>
    </section>
    <section v-if="exhibition.length > 0" class="mt-10 md:mt-20">
      <div class="flex items-center justify-between">
        <h2 class="text-mobile-2xl font-semibold md:text-2xl">
          {{ $t('index.exhibition_title') }}
        </h2>
      </div>
      <span
        v-if="locale === 'fr'"
        class="markdown-links mt-2 text-mobile-lg md:mt-4 md:text-lg"
        v-html="exhibitionText"
      />
      <span
        v-else
        class="markdown-links mt-2 text-mobile-lg md:mt-4 md:text-lg"
        v-html="exhibitionTextEn"
      />
      <div v-if="pending" class="mx-auto flex">
        <carousel
          :items-to-show="2"
          :items-to-scroll="1"
          :breakpoints="breakpoints"
        >
          <slide v-for="i in 4" :key="i">
            <CardSkeleton />
          </slide>
        </carousel>
      </div>
      <div v-else>
        <carousel
          :items-to-show="2"
          :items-to-scroll="1"
          :breakpoints="breakpoints"
        >
          <slide v-for="card in exhibition" :key="card.id">
            <CardResource :key="card.id" :resource="card" />
          </slide>

          <template #addons>
            <navigation />
            <!-- <pagination /> -->
          </template>
        </carousel>
      </div>
    </section>
    <section class="mt-10 md:mt-20">
      <div class="flex items-center justify-between">
        <h2 class="text-mobile-2xl font-semibold md:text-2xl">
          {{ $t('index.latest_publications_title') }}
        </h2>
      </div>
      <div v-if="pending" class="mx-auto flex">
        <carousel
          :items-to-show="2"
          :items-to-scroll="1"
          :breakpoints="breakpoints"
        >
          <slide v-for="i in 4" :key="i">
            <CardSkeleton />
          </slide>
        </carousel>
      </div>
      <div v-else>
        <div id="latest_publications" class="-mt-24 mb-36" />
        <div v-for="publicationGroup in latestPublicationsGroups">
          <div
            v-if="
              latestPublications.filter(publicationGroup.filterFn).length > 0
            "
          >
            <h2
              v-if="locale === 'fr'"
              class="text-mobile-lg font-semibold md:text-lg"
            >
              {{ publicationGroup.title }}
            </h2>
            <h2 v-else class="text-mobile-lg font-semibold md:text-lg">
              {{ publicationGroup.titleEn }}
            </h2>
            <carousel
              v-if="
                latestPublications.filter(publicationGroup.filterFn).length > 0
              "
              :items-to-show="2"
              :items-to-scroll="1"
              :breakpoints="breakpoints"
            >
              <slide
                v-for="card in latestPublications.filter(
                  publicationGroup.filterFn
                )"
                :key="card.id"
              >
                <CardResource :key="card.id" :resource="card" />
              </slide>

              <template #addons>
                <navigation />
                <!-- <pagination /> -->
              </template>
            </carousel>
          </div>
        </div>
      </div>
    </section>
    <section class="mt-10 md:mt-20">
      <div class="flex items-center justify-between">
        <h2 class="text-mobile-2xl font-semibold md:text-2xl">
          {{ $t('index.flora_title') }}
        </h2>
      </div>
      <span
        v-if="locale === 'fr'"
        class="markdown-links mt-2 text-mobile-lg md:mt-4 md:text-lg"
        v-html="floraText"
      />
      <span
        v-else
        class="markdown-links mt-2 text-mobile-lg md:mt-4 md:text-lg"
        v-html="floraTextEn"
      />
      <div v-if="pending" class="mx-auto flex">
        <carousel
          :items-to-show="2"
          :items-to-scroll="1"
          :breakpoints="breakpoints"
        >
          <slide v-for="i in 4" :key="i">
            <CardSkeleton />
          </slide>
        </carousel>
      </div>
      <div v-else>
        <carousel
          :items-to-show="2"
          :items-to-scroll="1"
          :breakpoints="breakpoints"
        >
          <slide v-for="card in floras" :key="card.id">
            <CardResource :key="card.id" :resource="card" />
          </slide>

          <template #addons>
            <navigation />
            <!-- <pagination /> -->
          </template>
        </carousel>
      </div>
    </section>
  </div>
</template>
<style>
.carousel__slide {
  display: block;
  padding: 10px;
}

.carousel__icon {
  background-color: white;
  fill: orangered;
}

.markdown-links a {
  color: rgba(242, 98, 36, 1);
}

.markdown-links a:hover {
  text-decoration: underline;
}
</style>
